/**
 * Festival-local time. The festival happens on Vorrå, so every clock, weekday
 * and day grouping is shown in Europe/Oslo — whatever the time zone of the
 * server (Fly runs in UTC) or the visitor. Using an explicit zone also keeps
 * server and client renders identical, so hydration never has to patch text.
 */
export const FESTIVAL_TIME_ZONE = 'Europe/Oslo'

type DateInput = Date | string | number

const toDate = (date: DateInput) =>
	date instanceof Date ? date : new Date(date)

const pad = (n: number) => String(n).padStart(2, '0')

const clockFormat = new Intl.DateTimeFormat('nb-NO', {
	hour: '2-digit',
	minute: '2-digit',
	hour12: false,
	timeZone: FESTIVAL_TIME_ZONE,
})

/**
 * Nynorsk weekday names, Sunday first. Spelled out instead of
 * `Intl.DateTimeFormat('nn')`, whose locale data not every browser ships —
 * a fallback there would differ from the server render. (Month names are the
 * same in bokmål and nynorsk, so those still come from `Intl`.)
 */
const WEEKDAYS = [
	'sundag',
	'måndag',
	'tysdag',
	'onsdag',
	'torsdag',
	'fredag',
	'laurdag',
]
const SHORT_WEEKDAYS = ['sun.', 'mån.', 'tys.', 'ons.', 'tor.', 'fre.', 'lau.']

/** 0 = Sunday … 6 = Saturday, on Vorrå's calendar. */
const festivalWeekday = (date: DateInput) => {
	const { year, month, day } = festivalParts(date)
	return new Date(Date.UTC(year, month - 1, day)).getUTCDay()
}

const dayMonthFormat = new Intl.DateTimeFormat('nb-NO', {
	day: 'numeric',
	month: 'long',
	timeZone: FESTIVAL_TIME_ZONE,
})

const partsFormat = new Intl.DateTimeFormat('en-CA', {
	year: 'numeric',
	month: '2-digit',
	day: '2-digit',
	hour: '2-digit',
	minute: '2-digit',
	hourCycle: 'h23',
	timeZone: FESTIVAL_TIME_ZONE,
})

/** "20:00" */
export const formatClock = (date: DateInput) =>
	clockFormat.format(toDate(date)).replace('.', ':')

/** "13:00–16:00", or just "20:00" when the end is unknown */
export const formatClockSpan = (start: DateInput, end?: DateInput | null) =>
	end ? `${formatClock(start)}–${formatClock(end)}` : formatClock(start)

/**
 * "16:00" → the first instant after `start` when the clock on Vorrå reads
 * 16:00: the same day, or the next one for an end past midnight. `null` for
 * anything that isn't a clock time, one Vorrå skips (spring-forward), or an
 * invalid start.
 */
export function festivalClockAfter(start: DateInput, clock: string) {
	const match = /^(\d{1,2}):(\d{2})$/.exec(clock.trim())
	if (!match) return null
	const hour = Number(match[1])
	const minute = Number(match[2])
	if (hour > 23 || minute > 59) return null
	const from = toDate(start)
	if (Number.isNaN(from.getTime())) return null
	const { year, month, day } = festivalParts(from)
	for (const dayOffset of [0, 1]) {
		// Oslo is UTC+1 or UTC+2: keep the offset that lands on the clock
		for (const offsetHours of [1, 2]) {
			const candidate = new Date(
				Date.UTC(year, month - 1, day + dayOffset, hour - offsetHours, minute),
			)
			const parts = festivalParts(candidate)
			if (
				parts.hour === hour &&
				parts.minute === minute &&
				candidate.getTime() > from.getTime()
			) {
				return candidate
			}
		}
	}
	return null
}

/** "laurdag" */
export const formatWeekday = (date: DateInput) =>
	WEEKDAYS[festivalWeekday(date)]!

/** "lau." */
export const formatShortWeekday = (date: DateInput) =>
	SHORT_WEEKDAYS[festivalWeekday(date)]!

/** "27. november" */
export const formatDayMonth = (date: DateInput) =>
	dayMonthFormat.format(toDate(date))

/**
 * "18.10.2026, 23:51" — when something happened (a booking, a payment), on
 * Vorrå's clock. Built from parts, not `toLocaleString`, so the server and
 * every browser print the same text.
 */
export function formatTimestamp(date: DateInput) {
	const { year, month, day } = festivalParts(date)
	return `${pad(day)}.${pad(month)}.${year}, ${formatClock(date)}`
}

/** Calendar parts of an instant, as seen on a clock on Vorrå. */
export function festivalParts(date: DateInput) {
	const parts = Object.fromEntries(
		partsFormat
			.formatToParts(toDate(date))
			.filter((part) => part.type !== 'literal')
			.map((part) => [part.type, Number(part.value)]),
	) as Record<'year' | 'month' | 'day' | 'hour' | 'minute', number>
	return parts
}

/**
 * "2026-11-01T18:00" — an instant as an `<input type="datetime-local">`
 * value, on Vorrå's clock (not the browser's or the server's).
 */
export function toFestivalDateTimeLocal(date: DateInput) {
	const { year, month, day, hour, minute } = festivalParts(date)
	return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}`
}

/**
 * A `datetime-local` value read as Vorrå's clock → the instant, so what a
 * form shows and what it saves are the same moment wherever the server runs.
 * `null` for anything else and for a time Vorrå skips (spring-forward). The
 * hour that happens twice in October is the first one (summer time).
 */
export function fromFestivalDateTimeLocal(value: string) {
	const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(
		value.trim(),
	)
	if (!match) return null
	const [year, month, day, hour, minute] = match.slice(1, 6).map(Number) as [
		number,
		number,
		number,
		number,
		number,
	]
	const second = Number(match[6] ?? 0)
	// Oslo is UTC+2 or UTC+1: keep the offset that lands on the clock
	for (const offsetHours of [2, 1]) {
		const candidate = new Date(
			Date.UTC(year, month - 1, day, hour - offsetHours, minute, second),
		)
		const parts = festivalParts(candidate)
		if (
			parts.year === year &&
			parts.month === month &&
			parts.day === day &&
			parts.hour === hour &&
			parts.minute === minute
		) {
			return candidate
		}
	}
	return null
}

/** "2026-11-27" — the festival-local calendar day, for grouping and keys. */
export function festivalDayKey(date: DateInput) {
	const { year, month, day } = festivalParts(date)
	return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export type FestivalDay<T> = {
	/** "2026-11-27" */
	key: string
	/** first event's start — use it for weekday/date labels */
	date: Date
	events: T[]
}

/** Events grouped per festival-local day, days and events in time order. */
export function groupByFestivalDay<T extends { date: DateInput }>(
	events: T[],
): FestivalDay<T>[] {
	const sorted = [...events].sort(
		(a, b) => toDate(a.date).getTime() - toDate(b.date).getTime(),
	)
	const days: FestivalDay<T>[] = []
	for (const event of sorted) {
		const key = festivalDayKey(event.date)
		const day = days.at(-1)
		if (day?.key === key) day.events.push(event)
		else days.push({ key, date: toDate(event.date), events: [event] })
	}
	return days
}

const monthName = (date: Date) =>
	new Intl.DateTimeFormat('nb-NO', {
		month: 'long',
		timeZone: FESTIVAL_TIME_ZONE,
	}).format(date)

/**
 * "27.–29. november 2026", "30. oktober – 1. november 2026" or
 * "1. november 2026" — the span covered by the first and last concert.
 */
export function formatFestivalSpan(first: DateInput, last: DateInput) {
	const a = festivalParts(first)
	const b = festivalParts(last)
	const firstDate = toDate(first)
	const lastDate = toDate(last)
	if (a.year === b.year && a.month === b.month) {
		return a.day === b.day
			? `${a.day}. ${monthName(firstDate)} ${a.year}`
			: `${a.day}.–${b.day}. ${monthName(firstDate)} ${a.year}`
	}
	if (a.year === b.year) {
		return `${a.day}. ${monthName(firstDate)} – ${b.day}. ${monthName(lastDate)} ${b.year}`
	}
	return `${a.day}. ${monthName(firstDate)} ${a.year} – ${b.day}. ${monthName(lastDate)} ${b.year}`
}

/**
 * Start of the festival-local day after `date` (Oslo midnight), as an
 * instant. Used as "the evening is over" boundary for a concert day.
 */
export function startOfNextFestivalDay(date: DateInput) {
	const { year, month, day } = festivalParts(date)
	// Oslo is UTC+1 or UTC+2; probe both offsets and keep the one that lands
	// exactly on local midnight of the next day.
	for (const offsetHours of [1, 2]) {
		const candidate = new Date(
			Date.UTC(year, month - 1, day + 1, 0 - offsetHours, 0, 0),
		)
		const parts = festivalParts(candidate)
		if (parts.hour === 0 && parts.minute === 0) return candidate
	}
	return new Date(Date.UTC(year, month - 1, day + 1, -1, 0, 0))
}
